import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from 'src/components/app-context';

import { Breadcrumbs } from '@prisjakt/ui/breadcrumbs';
import { PrimaryButton } from '@prisjakt/ui/button/variants';
import { TitleMediumText } from '@prisjakt/ui/text/variants';

import Loader from 'src/components/loader';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import { usePageMetaTags } from 'src/hooks/use-page-meta-tags';
import { useRouteLoaded } from 'src/hooks/use-route-loaded';
import type { AlertUnsubscribeMutation } from 'src/types';
import { useAlertUnsubscribeMutation } from '../queries/useAlertUnsubscribeMutation';

import PageContent from 'src/components/page/page-content';

import {
  Body,
  BreadCrumbWrapper,
  Content,
  Footer,
  Header,
  ProductLink,
} from './styles';

export const AlertUnsubscribePageLoader = () => {
  const route = useMatchedRoute();

  const { data, isLoading, isSuccess, mutate } = useAlertUnsubscribeMutation(
    route!.variables
  );

  useEffect(() => {
    mutate();
  }, []);

  useRouteLoaded(data);
  usePageMetaTags(data);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent>
      {isLoading ? <Loader /> : null}
      {isSuccess && data ? <AlertUnsubscribePage data={data} /> : null}
    </PageContent>
  );
};

const AlertUnsubscribePage = ({ data }: { data: AlertUnsubscribeMutation }) => {
  const { t } = useAppContext();
  const { product } = data.alertUnsubscribe!;

  return (
    <>
      <BreadCrumbWrapper>
        <Breadcrumbs
          containerProps={{
            onClick: (nextPage: string) => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.BREADCRUMB,
                nextPage,
              });
            },
          }}
        >
          <Link to="/">{t('common', 'breadcrumb.home')}</Link>
          <span>{t('alerts', 'alerts.title')}</span>
        </Breadcrumbs>
      </BreadCrumbWrapper>

      <Content>
        <Header>
          <TitleMediumText>
            {t('alerts', 'unsubscribe.removed')}
          </TitleMediumText>
          <Body>
            <span>{t('alerts', 'unsubscribe.no-more-notifications')}</span>
            <ProductLink to={product?.pathName ?? ''}>
              {product?.name}
            </ProductLink>
          </Body>
        </Header>

        <Footer>
          <PrimaryButton container={Link} containerProps={{ to: '/' }}>
            {t('common', 'button.go-home')}
          </PrimaryButton>
        </Footer>
      </Content>
    </>
  );
};
